import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Badge, Input, Select, Grid, StatCard, PageHeader, Modal } from '../../ui/components';
import { RefreshCw, Eye } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';
import { supabase } from '../../lib/supabase';
import { AppLayout } from '../../components/layout';
import { REFERRAL_NAV } from '../../config/navigation';

const statusColors = {
  clicked: 'neutral',
  started: 'primary',
  in_progress: 'warning',
  submitted: 'primary',
  approved: 'success',
  funded: 'success',
  withdrawn: 'danger',
};

const statusLabels = {
  clicked: 'Link Clicked',
  started: 'App Started',
  in_progress: 'In Progress',
  submitted: 'Submitted',
  approved: 'Approved',
  funded: 'Funded',
  withdrawn: 'Withdrawn',
};

const ReferralsList = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const session = localStorage.getItem('lk_partner_session');
    if (!session) {
      navigate('/partner/login');
      return;
    }
    
    const partnerData = JSON.parse(session);
    setPartner(partnerData);
    fetchReferrals(partnerData.id);
  }, [navigate]);

  useEffect(() => {
    filterReferrals();
  }, [referrals, searchTerm, statusFilter]);

  const fetchReferrals = async (partnerId) => {
    setLoading(true);
    try {
      const { data: referralsData } = await supabase
        .from('referrals')
        .select('*, loan_applications(id, status, loan_amount, readiness_score, created_at, product_type)')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });

      if (referralsData) {
        setReferrals(referralsData);
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterReferrals = () => {
    let filtered = referrals;

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(r => 
        r.applicant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.applicant_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredReferrals(filtered);
  };

  const handleLogout = () => {
    localStorage.removeItem('lk_partner_session');
    navigate('/partner/login');
  };

  const handleViewDetails = (referral) => {
    setSelectedReferral(referral);
    setDetailsOpen(true);
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (!partner) return null;

  return (
    <AppLayout
      role="referral"
      navItems={REFERRAL_NAV}
      user={{
        name: `${partner.firstName} ${partner.lastName}`,
        email: partner.email,
        role: 'referral',
      }}
      onLogout={handleLogout}
    >
      <div>
        <PageHeader
          title="My Referrals"
          description="Track all your referrals and their progress"
          actions={<Button variant="primary" icon={RefreshCw} onClick={() => fetchReferrals(partner.id)}>Refresh</Button>}
        />

        {/* Stats Cards */}
        <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
          <StatCard label="Total Referrals" value={referrals.length} />
          <StatCard label="Active" value={referrals.filter(r => ['started', 'in_progress', 'submitted'].includes(r.status)).length} />
          <StatCard label="Successful" value={referrals.filter(r => ['approved', 'funded'].includes(r.status)).length} />
          <StatCard label="Conversion Rate" value={`${referrals.length > 0 ? Math.round((referrals.filter(r => ['approved', 'funded'].includes(r.status)).length / referrals.length) * 100) : 0}%`} />
        </Grid>

        {/* Filters */}
        <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--lk-space-3)', alignItems: 'center' }}>
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="clicked">Link Clicked</option>
              <option value="started">App Started</option>
              <option value="in_progress">In Progress</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="funded">Funded</option>
              <option value="withdrawn">Withdrawn</option>
            </Select>
            <div style={{ textAlign: 'right', color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>
              Showing {filteredReferrals.length} of {referrals.length}
            </div>
          </div>
        </Surface>

        {/* Referrals Table */}
        <Surface padding="0" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="lk-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Date Referred</th>
                  <th>Status</th>
                  <th>Product</th>
                  <th>Loan Amount</th>
                  <th>Readiness Score</th>
                  <th>Progress</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 'var(--lk-space-8)' }}>
                      <div className="lk-spinner lk-spinner-sm" style={{ display: 'inline-block', marginRight: 8 }} />
                      Loading referrals...
                    </td>
                  </tr>
                ) : filteredReferrals.length > 0 ? (
                  filteredReferrals.map((referral) => {
                    const progress =
                      referral.status === 'clicked' ? 10 :
                      referral.status === 'started' ? 25 :
                      referral.status === 'in_progress' ? 50 :
                      referral.status === 'submitted' ? 75 :
                      referral.status === 'approved' ? 90 :
                      referral.status === 'funded' ? 100 : 0;
                    const isComplete = ['approved', 'funded'].includes(referral.status);
                    const score = referral.loan_applications?.readiness_score;
                    const scoreColor = score >= 80 ? 'success' : score >= 60 ? 'warning' : 'danger';

                    return (
                      <tr key={referral.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--lk-primary-100)', color: 'var(--lk-primary-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--lk-text-xs)', fontWeight: 600, flexShrink: 0 }}>
                              {referral.applicant_name?.[0] || referral.applicant_email?.[0] || '?'}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500 }}>{referral.applicant_name || 'Anonymous'}</div>
                              <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{referral.applicant_email || 'No email'}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>{new Date(referral.created_at).toLocaleDateString()}</div>
                          <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{getTimeAgo(referral.created_at)}</div>
                        </td>
                        <td>
                          <Badge variant={statusColors[referral.status] || 'neutral'}>
                            {statusLabels[referral.status] || referral.status}
                          </Badge>
                        </td>
                        <td>
                          {referral.loan_applications?.product_type ? (
                            <Badge variant="neutral">
                              {referral.loan_applications.product_type.replace(/_/g, ' ')}
                            </Badge>
                          ) : '-'}
                        </td>
                        <td style={{ fontWeight: 600 }}>
                          {referral.loan_applications?.loan_amount
                            ? `$${referral.loan_applications.loan_amount.toLocaleString()}`
                            : '-'}
                        </td>
                        <td>
                          {score ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                              <span style={{ fontWeight: 600 }}>{score}</span>
                              <div style={{ width: 60, height: 6, borderRadius: 3, background: 'var(--lk-gray-100)' }}>
                                <div style={{ height: '100%', width: `${score}%`, borderRadius: 3, background: `var(--lk-${scoreColor}-500)` }} />
                              </div>
                            </div>
                          ) : '-'}
                        </td>
                        <td>
                          <div style={{ width: 100, height: 8, borderRadius: 4, background: 'var(--lk-gray-100)' }}>
                            <div style={{ height: '100%', width: `${progress}%`, borderRadius: 4, background: isComplete ? 'var(--lk-success-500)' : 'var(--lk-primary-500)', transition: 'width 0.4s ease' }} />
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleViewDetails(referral)} />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-8)' }}>
                      No referrals found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Surface>

        {/* Details Modal */}
        <Modal open={detailsOpen} onClose={() => setDetailsOpen(false)} title="Referral Details">
          {selectedReferral && (
            <>
              <Grid cols={2} gap="var(--lk-space-3)" style={{ marginBottom: 'var(--lk-space-3)' }}>
                <Surface padding="var(--lk-space-4)">
                  <h6 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-3)' }}>Applicant Info</h6>
                  <div style={{ marginBottom: 'var(--lk-space-2)' }}>
                    <strong>Name:</strong> {selectedReferral.applicant_name || 'Anonymous'}
                  </div>
                  <div style={{ marginBottom: 'var(--lk-space-2)' }}>
                    <strong>Email:</strong> {selectedReferral.applicant_email || 'Not provided'}
                  </div>
                  <div style={{ marginBottom: 'var(--lk-space-2)' }}>
                    <strong>Referred:</strong> {new Date(selectedReferral.created_at).toLocaleDateString()}
                  </div>
                  <div>
                    <strong>Status:</strong>{' '}
                    <Badge variant={statusColors[selectedReferral.status]}>
                      {statusLabels[selectedReferral.status]}
                    </Badge>
                  </div>
                </Surface>
                <Surface padding="var(--lk-space-4)">
                  <h6 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-3)' }}>Application Info</h6>
                  {selectedReferral.loan_applications ? (
                    <>
                      <div style={{ marginBottom: 'var(--lk-space-2)' }}>
                        <strong>Product:</strong> {selectedReferral.loan_applications.product_type?.replace(/_/g, ' ') || '-'}
                      </div>
                      <div style={{ marginBottom: 'var(--lk-space-2)' }}>
                        <strong>Loan Amount:</strong> ${selectedReferral.loan_applications.loan_amount?.toLocaleString() || '-'}
                      </div>
                      <div style={{ marginBottom: 'var(--lk-space-2)' }}>
                        <strong>Readiness Score:</strong>{' '}
                        <Badge variant={
                          selectedReferral.loan_applications.readiness_score >= 80 ? 'success' :
                          selectedReferral.loan_applications.readiness_score >= 60 ? 'warning' : 'danger'
                        }>
                          {selectedReferral.loan_applications.readiness_score || 0}
                        </Badge>
                      </div>
                      <div>
                        <strong>App Status:</strong> {selectedReferral.loan_applications.status?.replace(/_/g, ' ') || '-'}
                      </div>
                    </>
                  ) : (
                    <div style={{ color: 'var(--lk-text-tertiary)' }}>Application not started yet</div>
                  )}
                </Surface>
              </Grid>

              <Surface padding="var(--lk-space-4)">
                <h6 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-3)' }}>Progress Timeline</h6>
                {(() => {
                  const p =
                    selectedReferral.status === 'clicked' ? 10 :
                    selectedReferral.status === 'started' ? 25 :
                    selectedReferral.status === 'in_progress' ? 50 :
                    selectedReferral.status === 'submitted' ? 75 :
                    selectedReferral.status === 'approved' ? 90 :
                    selectedReferral.status === 'funded' ? 100 : 0;
                  const c = ['approved', 'funded'].includes(selectedReferral.status);
                  return (
                    <div style={{ position: 'relative', height: 20, borderRadius: 10, background: 'var(--lk-gray-100)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${p}%`, borderRadius: 10, background: c ? 'var(--lk-success-500)' : 'var(--lk-primary-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 'var(--lk-text-xs)', fontWeight: 600, transition: 'width 0.6s ease' }}>
                        {statusLabels[selectedReferral.status]}
                      </div>
                    </div>
                  );
                })()}
              </Surface>
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--lk-space-4)' }}>
            <Button variant="secondary" onClick={() => setDetailsOpen(false)}>Close</Button>
          </div>
        </Modal>
      </div>

      <FloatingOwenAssistant context="referral-list" />
      <FloatingKnowledge context="general" />
    </AppLayout>
  );
};

export default ReferralsList;
